import { axiosBaseQuery } from '@client';
import { AccountingCodeFormValues } from '@containers';
import { AccountingCodeResponse } from '../../types/AccountingCodeResponse';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { BuyerAccountingCodesStateManagementFormValues } from '../../../sitemap/secured/admin/support/buyer/fragment/AdminSupportBuyerBulkAccountingCodeStateEditModal';
import { FleetResponseDto } from '@state';

export const adminAccountingCodesApi = createApi({
  baseQuery: axiosBaseQuery(),
  tagTypes: ['AdminAccountingCodes', 'AdminAccountingCodesVessels'],
  reducerPath: 'admin-accounting-codes_api',
  endpoints: (build) => ({
    fetchAdminAccountingCode: build.query<AccountingCodeResponse, number>({
      query: (accountingCodeId) => ({
        url: `/secured/accounting-code/${accountingCodeId}/load`,
        method: 'GET'
      }),
      providesTags: ['AdminAccountingCodes']
    }),
    submitAccountingCode: build.mutation<
      AccountingCodeResponse,
      AccountingCodeFormValues
    >({
      query: (payload) => ({
        url: '/secured/accounting-code/save',
        method: 'POST',
        data: payload
      }),
      invalidatesTags: ['AdminAccountingCodes']
    }),
    addBulkAccountingCodesWithCsv: build.mutation<
      string,
      {
        buyerId: number;
        data: FormData;
      }
    >({
      query: ({ buyerId, data }) => ({
        url: `/secured/buyer/${buyerId}/accounting-codes/upload-attachment`,
        method: 'POST',
        data: data,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        formData: true
      })
    }),
    downloadAccountingCodesOfBuyer: build.mutation<
      Blob,
      {
        buyerId: number;
      }
    >({
      query: ({ buyerId }) => ({
        url: `/secured/buyer/${buyerId}/accounting-codes/download-csv`,
        method: 'GET',
        cache: 'no-cache'
      })
    }),
    updateAccountingCodeStates: build.mutation<
      void,
      {
        buyerId: number;
        data: BuyerAccountingCodesStateManagementFormValues;
      }
    >({
      query: ({ buyerId, data }) => ({
        url: `/secured/buyer/${buyerId}/accounting-codes/update-state`,
        method: 'POST',
        data: data
      })
    }),
    fetchAccountingCodeVessels: build.query<FleetResponseDto[], number>({
      query: (accountingCodeId) => ({
        url: `/secured/accounting-codes/${accountingCodeId}/vessels`,
        method: 'GET'
      }),
      providesTags: (res, _error, accountingCodeId) =>
        res
          ? [
              {
                type: 'AdminAccountingCodesVessels',
                id: String(accountingCodeId)
              }
            ]
          : ['AdminAccountingCodesVessels']
    }),
    updateAccountingCodeVessels: build.mutation<
      FleetResponseDto[],
      { accountingCodeId: number; payload: number[] }
    >({
      query: ({ accountingCodeId, payload }) => ({
        url: `/secured/accounting-codes/${accountingCodeId}/vessels`,
        method: 'PUT',
        data: { vesselIds: payload }
      }),
      invalidatesTags: (res, _error, { accountingCodeId }) =>
        res
          ? [
              {
                type: 'AdminAccountingCodesVessels',
                id: String(accountingCodeId)
              }
            ]
          : ['AdminAccountingCodesVessels']
    })
  })
});

export type StateWithAdminAccountingCodesApi = {
  [adminAccountingCodesApi.reducerPath]: ReturnType<
    typeof adminAccountingCodesApi.reducer
  >;
};

export const {
  useFetchAdminAccountingCodeQuery,
  useSubmitAccountingCodeMutation,
  useAddBulkAccountingCodesWithCsvMutation,
  useDownloadAccountingCodesOfBuyerMutation,
  useUpdateAccountingCodeStatesMutation,
  useFetchAccountingCodeVesselsQuery,
  useUpdateAccountingCodeVesselsMutation
} = adminAccountingCodesApi;
